import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { DashboardLayout } from '../components/layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Video,
    Play,
    Calendar,
    Clock,
    MessageCircle,
    ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryInfo = {
    weekly_review: { icon: Video, color: 'bg-blue-50 text-blue-600' },
    qa_replay: { icon: MessageCircle, color: 'bg-purple-50 text-purple-600' }
};

export const LiveSessionPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { t, language, getLocalizedField } = useLanguage();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSession, setSelectedSession] = useState(null);

    const currentCategory = searchParams.get('category') || 'all';

    useEffect(() => {
        fetchSessions();
    }, [currentCategory]);

    const fetchSessions = async () => {
        setLoading(true);
        try {
            const url = currentCategory === 'all'
                ? `${API}/live-sessions`
                : `${API}/live-sessions?category=${currentCategory}`;
            const response = await axios.get(url);
            setSessions(response.data);
        } catch (error) {
            console.error('Error fetching sessions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCategoryChange = (category) => {
        if (category === 'all') {
            searchParams.delete('category');
        } else {
            searchParams.set('category', category);
        }
        setSearchParams(searchParams);
        setSelectedSession(null);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-12 w-full" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[1, 2, 3, 4].map((i) => (
                            <Skeleton key={i} className="h-48" />
                        ))}
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6 animate-fade-in">
                {/* Header */}
                <div>
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-slate-900">
                        {t('liveSession')}
                    </h1>
                    <p className="text-slate-500 mt-1">
                        {language === 'id' 
                            ? 'Tonton ulang sesi live dan Q&A'
                            : 'Watch live session replays and Q&A'
                        }
                    </p>
                </div>

                {/* Category Tabs */}
                <Tabs value={currentCategory} onValueChange={handleCategoryChange}>
                    <TabsList className="w-full md:w-auto grid grid-cols-3 md:flex">
                        <TabsTrigger value="all" data-testid="tab-all">
                            {language === 'id' ? 'Semua' : 'All'}
                        </TabsTrigger>
                        <TabsTrigger value="weekly_review" data-testid="tab-weekly-review">
                            {t('weeklyReview')}
                        </TabsTrigger>
                        <TabsTrigger value="qa_replay" data-testid="tab-qa-replay">
                            {t('qaReplay')}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value={currentCategory} className="mt-6">
                        {selectedSession ? (
                            // Video Player View
                            <div className="max-w-4xl mx-auto space-y-6">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSelectedSession(null)}
                                >
                                    <ChevronRight className="w-4 h-4 mr-1 rotate-180" />
                                    {t('back')}
                                </Button>

                                <Card className="overflow-hidden" data-testid="session-player">
                                    <div className="aspect-video bg-slate-900">
                                        {selectedSession.video_url ? (
                                            <video
                                                src={selectedSession.video_url}
                                                controls
                                                className="w-full h-full"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <div className="text-center">
                                                    <Play className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                                                    <p className="text-slate-400">
                                                        {language === 'id' ? 'Video belum tersedia' : 'Video not available yet'}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <CardContent className="pt-6">
                                        <h2 className="text-xl font-heading font-bold text-slate-900">
                                            {getLocalizedField(selectedSession, 'title')}
                                        </h2>
                                        <p className="text-slate-500 mt-2">
                                            {getLocalizedField(selectedSession, 'description')}
                                        </p>
                                        <div className="flex items-center gap-4 mt-4 text-sm text-slate-400">
                                            <span className="flex items-center gap-1">
                                                <Calendar className="w-4 h-4" />
                                                {formatDate(selectedSession.session_date)}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-4 h-4" />
                                                {selectedSession.duration_minutes} {t('minutes')}
                                            </span>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        ) : (
                            // List View
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {sessions.length === 0 ? (
                                    <div className="col-span-2 text-center py-12">
                                        <Video className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                                        <p className="text-slate-500">
                                            {language === 'id' 
                                                ? 'Belum ada sesi untuk kategori ini'
                                                : 'No sessions available for this category'
                                            }
                                        </p>
                                    </div>
                                ) : (
                                    sessions.map((session) => {
                                        const info = categoryInfo[session.category];
                                        const Icon = info?.icon || Video;

                                        return (
                                            <Card
                                                key={session.id}
                                                className="card-hover cursor-pointer overflow-hidden"
                                                onClick={() => setSelectedSession(session)}
                                                data-testid={`session-card-${session.id}`}
                                            >
                                                <div className="aspect-video bg-slate-900 relative">
                                                    <div className="absolute inset-0 flex items-center justify-center">
                                                        <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-sm">
                                                            <Play className="w-8 h-8 text-white" />
                                                        </div>
                                                    </div>
                                                    <div className="absolute bottom-4 right-4 bg-black/70 px-2 py-1 rounded text-white text-sm">
                                                        {session.duration_minutes} {t('minutes')}
                                                    </div>
                                                </div>
                                                <CardContent className="pt-4">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <div className={`w-6 h-6 rounded flex items-center justify-center ${info?.color || 'bg-slate-100'}`}>
                                                            <Icon className="w-3.5 h-3.5" />
                                                        </div>
                                                        <span className="text-xs text-slate-500">
                                                            {session.category === 'weekly_review' ? t('weeklyReview') : t('qaReplay')}
                                                        </span>
                                                    </div>
                                                    <h3 className="font-heading font-semibold text-slate-900 line-clamp-2">
                                                        {getLocalizedField(session, 'title')}
                                                    </h3>
                                                    <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        {formatDate(session.session_date)}
                                                    </p>
                                                </CardContent>
                                            </Card>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardLayout>
    );
};
